import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { useLanguage } from '@/hooks/use-language';
import { useGroups } from '@/hooks/use-groups';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ProtectedAdminRoute } from '@/components/admin/ProtectedAdminRoute';
import { SubgroupFormDialog } from '@/components/groups/SubgroupFormDialog';
import { getGroupColorClasses, getAdminColorClasses, getLocalizedGroupName } from '@/utils/groupColors';
import { useToast } from '@/hooks/use-toast';
import { Users, ArrowLeft, Settings, Phone, UserPlus, UserMinus, Plus, Mail, ChevronUp, ChevronDown, GripVertical, Loader2 } from 'lucide-react';
import { AddMemberDialog } from '@/components/groups/AddMemberDialog';
import { GroupMemberActions } from '@/components/groups/GroupMemberActions';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface Group {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  subgroupCount: number;
  upcomingEvents: number;
  isParent: boolean;
  parentGroup?: string;
  members: Array<{
    id: string;
    name: string;
    phone: string;
    email: string;
    role: string;
    isAdmin?: boolean;
  }>;
}

export default function Groups() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const viewType =
    location.state?.viewType ||
    (location.pathname.includes('subgroups') ? 'subgroups' : 'main');
  
  // Use Groups context
  const { groups: contextGroups, isLoading, createGroup, addMemberToGroup, removeMemberFromGroup, refreshGroups } = useGroups();
  
  // State for dialog management
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState<'create' | 'edit'>('create');
  const [selectedGroup, setSelectedGroup] = React.useState<Group | undefined>();
  const [expandedGroup, setExpandedGroup] = React.useState<string | null>(null);

  // State for add member dialog
  const [addMemberDialogOpen, setAddMemberDialogOpen] = React.useState(false);
  const [selectedGroupForMembers, setSelectedGroupForMembers] = React.useState<Group | null>(null);

  // State for available members
  const [allMembers, setAllMembers] = React.useState<any[]>([]);

  const isGroupAdmin = React.useCallback((group: Group) => {
    const uid = user?.id;
    if (!uid) return false;
    return group.members.some(m => m.id === uid && m.isAdmin);
  }, [user?.id]);

  // Transform context groups to local Group interface
  const groups = React.useMemo(() => {
    const list: Group[] = [];
    contextGroups.forEach(root => {
      list.push({
        id: root.id,
        name: root.name,
        description: root.description || '',
        memberCount: root.member_count,
        subgroupCount: root.subgroups?.length || 0,
        upcomingEvents: 0,
        isParent: true,
        members: (root.members || []).map(m => ({
          id: m.id,
          name: `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.phone || t('groups.unknownMember'),
          phone: m.phone || '',
          email: '', // email not available in current interface 
          role: m.role || 'Member',
          isAdmin: m.isAdmin || false
        }))
      });
      (root.subgroups || []).forEach(sg => {
        list.push({
          id: sg.id,
          name: sg.name,
          description: sg.description || '',
          memberCount: sg.member_count,
          subgroupCount: 0,
          upcomingEvents: 0,
          isParent: false,
          parentGroup: root.name,
          members: (sg.members || []).map(m => ({
            id: m.id,
            name: `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.phone || t('groups.unknownMember'),
            phone: m.phone || '',
            email: '', // email not available in current interface
            role: m.role || 'Member',
            isAdmin: m.isAdmin || false
          }))
        });
      });
    });
    return list;
  }, [contextGroups, t]);

  // Load all available members for adding to groups
  React.useEffect(() => {
    const loadMembers = async () => {
      try {
        const { data: profiles } = await supabase
          .from('profiles_public_v')
          .select('user_id, first_name, last_name, phone, email');
        
        const members = (profiles || []).map(p => ({
          id: Number(p.user_id), // Convert to number for compatibility
          name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.phone || t('groups.unknownMember'),
          phone: p.phone || '',
          email: p.email || '',
          groups: [], // TODO: Add actual group memberships
          subgroups: [], // TODO: Add actual subgroup memberships
          role: 'Member',
          isAdmin: false
        }));
        
        setAllMembers(members);
      } catch (error) {
        console.error('Failed to load members:', error);
      }
    };
    
    loadMembers();
  }, [t]);

  // Filter groups based on view type
  const visibleGroups = React.useMemo(() => {
    if (viewType === 'subgroups') {
      // Show all subgroups (non-parent groups)
      return groups.filter(group => !group.isParent);
    } else {
      // Show only parent groups
      return groups.filter(group => group.isParent);
    }
  }, [viewType, groups]);

  // Get parent groups for the dialog
  const parentGroups = React.useMemo(() => 
    groups.filter(group => group.isParent), [groups]
  );

  const pageTitle = viewType === 'subgroups' ? t('groups.subgroups.title') : t('groups.title');
  const pageDescription = viewType === 'subgroups' 
    ? t('groups.subgroups.subtitle')
    : t('groups.subtitle');

  const handleCreateGroup = () => {
    setDialogMode('create');
    setSelectedGroup(undefined);
    setDialogOpen(true);
  };

  const handleEditGroup = (group: Group) => {
    setDialogMode('edit');
    setSelectedGroup(group);
    setDialogOpen(true);
  };

  const handleGroupSubmit = async (groupData: Partial<Group>) => {
    try {
      if (dialogMode === 'create') {
        // Create subgroup under selectedGroup (if provided)
        const parentId = selectedGroup?.id;
        await createGroup(groupData.name!, groupData.description || '', parentId);
        toast({
          title: t('groups.create'),
          description: `${groupData.name} ${t('common.created')}`
        });
      }
      // Note: Edit functionality would require additional context method
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('groups.error'),
        variant: 'destructive'
      });
    }
  };

  const handleAddMember = (group: Group) => {
    setSelectedGroupForMembers(group);
    setAddMemberDialogOpen(true);
  };

  const handleAddMembers = async (memberIds: number[], role: string) => {
    if (!selectedGroupForMembers) return;

    try {
      // Add each selected member to the group
      for (const memberId of memberIds) {
        await addMemberToGroup(selectedGroupForMembers.id, memberId.toString());
      }

      toast({
        title: t('groups.memberAdded'),
        description: `${memberIds.length} member${memberIds.length !== 1 ? 's' : ''} added to ${getLocalizedGroupName(selectedGroupForMembers.name, language)}`
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('groups.error'),
        variant: 'destructive'
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6 pb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("nav.back")}
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">{pageTitle}</h1>
              <p className="text-muted-foreground mt-2">{pageDescription}</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            {t('groups.loading')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("nav.back")}
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">{pageTitle}</h1>
            <p className="text-muted-foreground mt-2">
              {pageDescription}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <ProtectedAdminRoute fallback={null}>
            <Button onClick={handleCreateGroup}>
              <Plus className="h-4 w-4 mr-2" />
              {t('groups.create')}
            </Button>
          </ProtectedAdminRoute>
        </div>
      </div>

      <div className="space-y-4">
        {visibleGroups.map((group, index) => (
          <Card 
            key={group.id} 
            className="overflow-hidden transition-all hover:shadow-md"
          >
            <CardHeader 
              className="cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  <span className="text-lg font-semibold text-foreground">
                    {group.memberCount} {t('groups.members')}
                  </span>
                  {group.subgroupCount > 0 && (
                    <>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-sm text-muted-foreground">
                        {group.subgroupCount} {t('groups.subgroups')}
                      </span>
                    </>
                  )}
                  {group.upcomingEvents > 0 && (
                    <>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-sm text-muted-foreground">
                        {group.upcomingEvents} {t('groups.events')}
                      </span>
                    </>
                  )}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {expandedGroup === group.id ? (
                    <>
                      <ChevronUp className="h-4 w-4 mr-1" />
                      {t('groups.collapseMembers')}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-1" />
                      {t('groups.expandMembers')}
                    </>
                  )}
                </Button>
              </div>

              <CardTitle className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  {getLocalizedGroupName(group.name, language)}
                  {(() => {
                    const colors = getGroupColorClasses(group.name);
                    return (
                      <Badge 
                        variant="outline" 
                        className={cn("text-xs border-transparent", colors.bg, colors.text)}
                      >
                        {group.isParent ? 'Main Group' : 'Subgroup'}
                      </Badge>
                    );
                  })()}
                  {!group.isParent && group.parentGroup && (
                    <Badge variant="outline" className="text-xs">
                      {getLocalizedGroupName(group.parentGroup, language)}
                    </Badge>
                  )}
                </div>
              </CardTitle>

              <CardDescription>
                {group.description}
              </CardDescription>
            </CardHeader>
            
            {/* Members List - Expanded View */}
            {expandedGroup === group.id && (
              <CardContent>
                <Separator className="mb-4" />
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm text-muted-foreground">
                      {t('groups.members')} ({group.members.length})
                    </h4>
                    <ProtectedAdminRoute fallback={null}>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleAddMember(group)}
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          {t('groups.addMember')}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleEditGroup(group)}
                        >
                          <Settings className="h-4 w-4 mr-2" />
                          {t('groups.edit')}
                        </Button>
                        {group.isParent && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleCreateGroup()}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            {t('groups.createSubgroup')}
                          </Button>
                        )}
                      </div>
                    </ProtectedAdminRoute>
                  </div>

                  {group.members.length === 0 ? (
                    <p className="text-muted-foreground text-center py-6">
                      {t('groups.noMembers')}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {group.members.map((member) => (
                        <div key={member.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className="space-y-1 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{member.name}</span>
                              {member.isAdmin ? (
                                <Badge variant="admin" className="text-xs">
                                  Admin
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs">
                                  {member.role}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              {member.phone && (
                                <div className="flex items-center gap-1">
                                  <Phone className="h-3 w-3" />
                                  {member.phone}
                                </div>
                              )}
                              {member.email && (
                                <div className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {member.email}
                                </div>
                              )}
                            </div>
                          </div>
                          <GroupMemberActions
                            groupId={group.id}
                            memberId={member.id}
                            memberName={member.name}
                            isCurrentUser={member.id === user?.id}
                            canRemove={user?.isAdmin || isGroupAdmin(group)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Group Form Dialog */}
      <SubgroupFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        subgroup={selectedGroup}
        parentGroups={parentGroups}
        onSubmit={handleGroupSubmit}
      />

      {/* Add Member Dialog */}
      <AddMemberDialog
        open={addMemberDialogOpen && !!selectedGroupForMembers}
        onOpenChange={setAddMemberDialogOpen}
        group={selectedGroupForMembers || groups[0]}
        allMembers={allMembers}
        onAddMembers={handleAddMembers}
      />
    </div>
  );
}
